import { useCallback, type Dispatch, type SetStateAction } from 'react';
import {
  AssistantContentType,
  AssistantFeedback,
  RequestStatus,
  SenderRole
} from '../helpers/enums';
import { sortMessageBlocks } from '../theme/utils';
import { type AssistantMessage, type Message } from '../types';
import {
  type ChatUpdateEventMessage,
  type ContentMessageBlocksData,
  type ContentMessageClarificationData,
  type ContentMessageInterimData,
  type ContentMessageOutOfScopeData
} from '../types/events';

interface UseChatEventHandlerProps {
  setThinkingState: Dispatch<SetStateAction<string | null>>;
  setInterimAnswer: Dispatch<SetStateAction<string | null>>;
  setMessages: Dispatch<SetStateAction<Message[]>>;
  setError: Dispatch<SetStateAction<string | null>>;
  updateConversation: (convId: string) => Promise<void>;
  loadConversations: () => Promise<void>;
  refreshQuota: () => Promise<void>;
  currentConvIdRef: React.MutableRefObject<string | null>;
}

/**
 * Custom hook to handle chat events from Pusher
 */
export const useChatEventHandler = ({
  setThinkingState,
  setInterimAnswer,
  setMessages,
  setError,
  updateConversation,
  loadConversations,
  refreshQuota,
  currentConvIdRef
}: UseChatEventHandlerProps) => {
  // Helper function to update or create a message
  const updateOrCreateMessage = useCallback(
    (
      messageId: string,
      updateFn: (existing: Message | undefined) => Partial<AssistantMessage>,
      defaultContent = ''
    ) => {
      setMessages((prev) => {
        const existingMessage = prev.find((msg) => msg.id === messageId);
        if (existingMessage) {
          return sortMessageBlocks(
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    ...updateFn(msg),
                    updated_at: new Date().toISOString()
                  }
                : msg
            )
          );
        } else {
          return sortMessageBlocks([
            ...prev,
            {
              id: messageId,
              message_type: SenderRole.ASSISTANT,
              content: defaultContent,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              response_blocks: [],
              ...updateFn(undefined)
            } as AssistantMessage
          ]);
        }
      });
    },
    [setMessages]
  );

  // Helper function to refresh data after completion/error
  const refreshAfterCompletion = useCallback(async () => {
    setThinkingState(null);
    setInterimAnswer(null);
    if (currentConvIdRef.current) {
      updateConversation(currentConvIdRef.current);
    }
    loadConversations();
    refreshQuota();
  }, [
    setThinkingState,
    setInterimAnswer,
    currentConvIdRef,
    updateConversation,
    loadConversations,
    refreshQuota
  ]);

  // Handle INTERIM_ANSWER content type
  const handleInterimAnswer = useCallback(
    (data: ChatUpdateEventMessage) => {
      const contentData = data.data as ContentMessageInterimData;
      setInterimAnswer(contentData.content);

      if (contentData.is_partial && data.message_id) {
        updateOrCreateMessage(
          data.message_id,
          () => ({
            content: contentData.content,
            status: RequestStatus.PROCESSING
          }),
          contentData.content
        );
      }
    },
    [setInterimAnswer, updateOrCreateMessage]
  );

  // Handle BLOCKS content type
  const handleBlocks = useCallback(
    (data: ChatUpdateEventMessage) => {
      const blocksData = data.data as ContentMessageBlocksData;
      if (data.message_id) {
        updateOrCreateMessage(data.message_id, () => ({
          response_blocks: blocksData.blocks || []
        }));
      }
    },
    [updateOrCreateMessage]
  );

  // Handle CLARIFICATION content type
  const handleClarification = useCallback(
    (data: ChatUpdateEventMessage) => {
      const clarificationData = data.data as ContentMessageClarificationData;

      if (clarificationData.is_partial) {
        setThinkingState(clarificationData.content);
      } else {
        setThinkingState(null);
        if (currentConvIdRef.current) {
          updateConversation(currentConvIdRef.current);
        }
      }

      if (data.message_id) {
        updateOrCreateMessage(
          data.message_id,
          () => ({
            content: clarificationData.content,
            status: RequestStatus.PROCESSING
          }),
          clarificationData.content
        );
      }
    },
    [setThinkingState, currentConvIdRef, updateConversation, updateOrCreateMessage]
  );

  // Handle OUT_OF_SCOPE content type
  const handleOutOfScope = useCallback(
    async (data: ChatUpdateEventMessage) => {
      const outOfScopeData = data.data as ContentMessageOutOfScopeData;

      if (data.message_id) {
        updateOrCreateMessage(
          data.message_id,
          () => ({
            content: outOfScopeData.content,
            status: RequestStatus.COMPLETED
          }),
          outOfScopeData.content
        );
      }

      await refreshAfterCompletion();
    },
    [updateOrCreateMessage, refreshAfterCompletion]
  );

  // Handle ERROR_RESPONSE content type
  const handleErrorResponse = useCallback(async () => {
    await refreshAfterCompletion();
  }, [refreshAfterCompletion]);

  // Handle TOKEN_LIMIT_ERROR content type
  const handleTokenLimitError = useCallback(async () => {
    // refresh quota
    await refreshQuota();
    await refreshAfterCompletion();
  }, [refreshQuota, refreshAfterCompletion]);

  // Handle CONTENT message type
  const handleContentMessage = useCallback(
    (data: ChatUpdateEventMessage) => {
      if (!data.message_id) return;

      const contentData = data.data as any;
      switch (contentData.content_type) {
        case AssistantContentType.INTERIM_ANSWER:
          handleInterimAnswer(data);
          break;
        case AssistantContentType.BLOCKS:
          handleBlocks(data);
          break;
        case AssistantContentType.CLARIFICATION:
          handleClarification(data);
          break;
        case AssistantContentType.OUT_OF_SCOPE:
          handleOutOfScope(data);
          break;
        case AssistantContentType.ERROR_RESPONSE:
          handleErrorResponse();
          break;
        case AssistantContentType.TOKEN_LIMIT_ERROR:
          handleTokenLimitError(data);
          break;
        default:
          break;
      }
    },
    [handleInterimAnswer, handleBlocks, handleClarification, handleOutOfScope, handleErrorResponse]
  );

  // Handle COMPLETION message type
  const handleCompletion = useCallback(
    async (data: ChatUpdateEventMessage) => {
      setThinkingState(null);
      setInterimAnswer(null);

      const completionData = data.data as any;
      if (data.message_id && completionData?.final_blocks) {
        setMessages((prev) =>
          sortMessageBlocks(
            prev.map((msg) =>
              msg.id === data.message_id
                ? {
                    ...msg,
                    response_blocks: completionData.final_blocks,
                    updated_at: new Date().toISOString()
                  }
                : msg
            )
          )
        );
      }

      if (currentConvIdRef.current) {
        updateConversation(currentConvIdRef.current);
      }
      loadConversations();
      refreshQuota();
    },
    [
      setThinkingState,
      setInterimAnswer,
      setMessages,
      currentConvIdRef,
      updateConversation,
      loadConversations,
      refreshQuota
    ]
  );

  // Handle ERROR message type
  const handleError = useCallback(
    async (data: ChatUpdateEventMessage) => {
      setThinkingState(null);
      setInterimAnswer(null);
      const errorData = data.data as any;
      setError(errorData.message);

      if (currentConvIdRef.current) {
        updateConversation(currentConvIdRef.current);
      }
      refreshQuota();
    },
    [
      setThinkingState,
      setInterimAnswer,
      setError,
      currentConvIdRef,
      updateConversation,
      refreshQuota
    ]
  );

  // Main event handler
  const handleConvChatEvents = useCallback(
    (data: ChatUpdateEventMessage) => {
      console.log('[AskVantage] Chat update received:', data);

      const messageData = data.data as any;

      switch (data.message_type) {
        case AssistantFeedback.THINKING:
          setThinkingState(messageData.message);
          break;
        case AssistantFeedback.CONTENT:
          handleContentMessage(data);
          break;
        case AssistantFeedback.COMPLETION:
          handleCompletion(data);
          break;
        case AssistantFeedback.ERROR:
          handleError(data);
          break;
        default:
          console.log('[AskVantage] Unknown message type:', data);
          break;
      }
    },
    [setThinkingState, handleContentMessage, handleCompletion, handleError]
  );

  return { handleConvChatEvents };
};
